/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx } from '@compiled/react';

import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { NewCoreIconProps } from '@atlaskit/icon/types';
import AiGenerativeRemix from '@atlaskit/icon-lab/core/ai-generative-remix';
import DiagramSymbolMindMapIcon from '@atlaskit/icon-lab/core/diagram-symbol-mind-map';
import WorldIcon from '@atlaskit/icon-lab/core/world';
import AppIcon from '@atlaskit/icon/core/app';
import ChartBarIcon from '@atlaskit/icon/core/chart-bar';
import {
	BUILTIN_TOOLBAR_KEYS,
	createEditorToolbarActions,
	type EditorToolbarHandlerContext,
	type ManifestEditorToolbarActions,
	type NativeEmbedParameterValues,
} from '@atlaskit/native-embeds-common';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import { getGlobalTheme } from '@atlaskit/tokens/get-global-theme';
import type { AIPlugin } from '@atlassian/editor-plugin-ai';
import { allNativeEmbedCoreFeatures } from '@atlassian/native-embeds-core';
import LoadingOverlay from '@atlassian/native-embeds-core/loading-overlay';
import {
	type Experience,
	type ExperienceManifest,
	experienceManifestId,
	type ExperienceManifestId,
	type LoadingComponentProps,
	type NativeEmbedChildCommand,
	type NativeEmbedFeature,
	type ResolvedExperienceMeta,
	type ShouldFocusOnClickOptions,
	type TransformUrlOptions,
} from '@atlassian/native-embeds-core/types';

const mauiLoadingIcons = [AiGenerativeRemix, ChartBarIcon, DiagramSymbolMindMapIcon, WorldIcon];

export const MAUI_EXPERIENCE_MANIFEST_ID: ExperienceManifestId = experienceManifestId('maui');

const MAUI_DEFAULT_WIDTH_KILLSWITCH = 'native_embeds_maui_default_width_killswitch';

const isMauiUrl = (url: string): boolean => /\/remix\/ui\/[A-Za-z0-9_-]+/.test(url);

/**
 * Experience manifest for a MAUI embed.
 * Register with getNativeEmbedFacade().registerExperienceManifest(MauiExperienceManifest),
 * then call getNativeEmbedFacade().createExperienceForUrl(url) to obtain an Experience for EmbedFrameUI.
 */
export const MauiExperienceManifest: {
	readonly allowMaxWidth: true;
	readonly handleChildCommands: (_experience: Experience, _cmd: NativeEmbedChildCommand) => void;
	readonly id: ExperienceManifestId;
	readonly isActive: () => true;
	readonly isUrlMatch: (url: string) => boolean;
	/** Function form — evaluated at runtime so `cc_maui_resize` can resolve after Statsig init. */
	readonly lockResizeAspectRatio: () => boolean;
	/**
	 * MAUI apps stay usable at very small widths, so opt into a much smaller
	 * narrow-fallback threshold than the platform default (480). Below this the
	 * embed collapses to a SmartCard block card in view mode.
	 */
	readonly narrowEmbedThreshold: 50;
	readonly parameterDefaults: {
		readonly width: 760 | undefined;
	};
	// remove this when MAUI is onboarded to the Linking Platform. This is here to allow testing before it is onboarded.
	readonly resolveViaLinkingPlatform: false;
	readonly shouldFocusOnClick: (
		url: string,
		meta: ResolvedExperienceMeta,
		options?: ShouldFocusOnClickOptions,
	) => boolean;
	readonly supportedFeatures: Set<string>;
	readonly transformUrl: (
		url: string,
		isFullScreen?: boolean,
		options?: TransformUrlOptions,
	) => string;
	readonly uiConfig: {
		readonly appearance: 'overlay';
		readonly editorToolbarActions: () => ManifestEditorToolbarActions<any>;
		readonly editorToolbarHandlers: {
			readonly onAskRovoClick: (
				_parameters: NativeEmbedParameterValues,
				context?: EditorToolbarHandlerContext,
			) => void;
		};
		readonly fullScreenDialogControls: {
			readonly hideOpenInNewTab: true;
			readonly hideTitleIcon: true;
		};
		readonly headerControls: {
			readonly hideMoreMenu: true;
			readonly hideTitle: true;
		};
		readonly renderLoadingComponent: (props: LoadingComponentProps) => JSX.Element;
		readonly showBorder: false;
		readonly showExpandButton: false;
		readonly titleIcon: {
			(props: NewCoreIconProps): JSX.Element;
			displayName: string;
		};
		readonly trackViewed: true;
	};
} = {
	allowMaxWidth: true,
	id: MAUI_EXPERIENCE_MANIFEST_ID,
	handleChildCommands: (_experience: Experience, _cmd: NativeEmbedChildCommand): void => {},
	isUrlMatch: isMauiUrl,
	supportedFeatures: new Set<NativeEmbedFeature>(allNativeEmbedCoreFeatures),
	isActive: (): true => true,
	lockResizeAspectRatio: () => !fg('cc_maui_resize'),
	narrowEmbedThreshold: 50,
	parameterDefaults: {
		get width(): 760 | undefined {
			return fg(MAUI_DEFAULT_WIDTH_KILLSWITCH) ? 760 : undefined;
		},
	},
	shouldFocusOnClick: (
		_url: string,
		_meta: ResolvedExperienceMeta,
		options?: ShouldFocusOnClickOptions,
	): boolean => !fg('cc_maui_editing') || options?.isSelected !== true,
	transformUrl: (url: string, _isFullScreen?: boolean, options?: TransformUrlOptions): string => {
		const urlObj = new URL(url);
		const { colorMode } = getGlobalTheme();

		if (colorMode) {
			urlObj.searchParams.set('theme', colorMode);
		}

		// When the host Confluence page is in edit mode, forward an
		// `enable-edits=` query param (empty value) so the embedded MAUI app
		// can render its edit-only affordances.
		if (options?.isEditMode) {
			urlObj.searchParams.set('enable-edits', '');
		}

		return urlObj.toString();
	},
	// remove this when MAUI is onboarded to the Linking Platform. This is here to allow testing before it is onboarded.
	resolveViaLinkingPlatform: false,
	uiConfig: {
		trackViewed: true,
		showBorder: false,
		showExpandButton: false,
		renderLoadingComponent: (props: LoadingComponentProps): JSX.Element => {
			// The exposure is fired at the toolbar click in `duplicate-and-replace-maui-app.ts`.
			if (expValEqualsNoExposure('cc-maui-ai-edit-loading-experiment', 'isEnabled', true)) {
				return (
					<LoadingOverlay
						variant="simple-bar"
						animationDisabled={props.animationDisabled}
						showBlanket={props.source === 'ai-edit'}
						testId={props.testId}
					/>
				);
			}
			return (
				<LoadingOverlay
					animationDisabled={props.animationDisabled}
					icons={mauiLoadingIcons}
					testId={props.testId}
				/>
			);
		},
		editorToolbarActions: () =>
			createEditorToolbarActions({
				customActions: {},
				items: [
					...(fg('cc-maui-phase-2')
						? fg('platform_editor_ai_remix_toolbar')
							? [
									BUILTIN_TOOLBAR_KEYS.ASK_ROVO,
									BUILTIN_TOOLBAR_KEYS.REMIX_VARIANT,
									BUILTIN_TOOLBAR_KEYS.SEPARATOR,
								]
							: [BUILTIN_TOOLBAR_KEYS.ASK_ROVO, BUILTIN_TOOLBAR_KEYS.SEPARATOR]
						: []),
					...(expValEquals('cc-maui-experiment', 'isEnabled', true)
						? fg('cc-maui-toolbar-separators-update')
							? [BUILTIN_TOOLBAR_KEYS.BORDER]
							: [BUILTIN_TOOLBAR_KEYS.BORDER, BUILTIN_TOOLBAR_KEYS.SEPARATOR]
						: []),
					BUILTIN_TOOLBAR_KEYS.ALIGNMENT,
					...(fg('cc-maui-toolbar-separators-update') ? [] : [BUILTIN_TOOLBAR_KEYS.SEPARATOR]),
					BUILTIN_TOOLBAR_KEYS.EXPAND,
					BUILTIN_TOOLBAR_KEYS.COPY,
					BUILTIN_TOOLBAR_KEYS.DELETE,
				],
				moreItems: [],
			}),
		editorToolbarHandlers: {
			onAskRovoClick: (
				_parameters: NativeEmbedParameterValues,
				context?: EditorToolbarHandlerContext,
			): void => {
				const editorApi = context?.editorApi as PublicPluginAPI<[AIPlugin]>;
				// Stage the embed as the inline-Rovo streaming target. The overlay
				// is NOT triggered here — `RovoPromptScreenWithLogic.onSubmit`
				// promotes this on prompt submit. `expValEquals` (NOT NoExposure)
				// because this toolbar click is the designated `cc-maui-experiment`
				// exposure point.
				if (
					// eslint-disable-next-line @atlaskit/platform/no-preconditioning -- gate + experiment layering for MAUI rollout (cc-maui-experiment + cc-maui-phase-2); will simplify post-rollout
					expValEquals('cc-maui-experiment', 'isEnabled', true) &&
					fg('cc-maui-phase-2')
				) {
					const localId = context?.selectedNodeLocalId;
					if (localId) {
						editorApi?.aiExperience?.actions?.stageInlineRovoStreaming?.(localId);
					}
				}
				editorApi?.aiExperience?.actions?.openAIModal?.({
					invokedFrom: 'floatingToolbar',
				});
			},
		},
		fullScreenDialogControls: {
			hideOpenInNewTab: true,
			hideTitleIcon: true,
		},
		headerControls: {
			hideMoreMenu: true,
			hideTitle: true,
		},
		titleIcon: AppIcon,
		appearance: 'overlay',
	},
} as const satisfies ExperienceManifest;
